import { useCallback } from 'react';
// Import store and translations synchronously
import { useLanguageStore, type Language as AppLanguage } from '@/stores/language.store';
import { translations, type Language, supportedLanguages } from '@/i18n/translations';

/** Pages marketing : jeux de clés limités à `supportedLanguages` ; sinon repli anglais. */
function resolveMarketingLanguage(lang: string): Language {
  if (supportedLanguages.includes(lang as Language)) return lang as Language;
  return 'en';
}

// Fonction pour obtenir une traduction avec support des variables et tableaux
function translate(key: string, language: string, params?: Record<string, string>): string | string[] {
  const keys = key.split('.');
  const lang = resolveMarketingLanguage(language);
  let value: any = translations[lang];

  for (const k of keys) {
    if (value && typeof value === 'object' && k in value) {
      value = value[k];
    } else {
      return key;
    }
  }

  if (typeof value === 'string' && params) {
    return Object.entries(params).reduce((acc, [k, v]) => acc.replace(new RegExp(`{{${k}}}`, 'g'), v), value);
  }

  return value;
}

export function useTranslation() {
  // S'abonner au store pour recevoir les mises à jour de langue
  const language = useLanguageStore((state) => state.language);
  const setLanguage = useLanguageStore((state) => state.setLanguage);

  /** Stable tant que `language` est inchangé — évite les boucles useEffect (ex. fetch) si `t` est en dépendance. */
  const t = useCallback(
    (key: string, params?: Record<string, string>): string | string[] => translate(key, language, params),
    [language],
  );

  const changeLanguage = (lang: AppLanguage) => {
    setLanguage(lang);
    // Forcer un re-render en changeant l'état du store
    window.dispatchEvent(new CustomEvent('languageChanged', { detail: { language: lang } }));
  };

  return {
    t,
    language,
    changeLanguage,
  };
}
